import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    OpenApiGetYandexMarketCampaignsMethod,
    type OpenApiGetYandexMarketCampaignsRequest,
    type OpenApiGetYandexMarketCampaignsResponseSuccess,
    OpenApiGetYandexMarketCampaignsUrl,
    OpenApiGetYandexMarketProductsSettingsMethod,
    type OpenApiGetYandexMarketProductsSettingsRequest,
    type OpenApiGetYandexMarketProductsSettingsResponseSuccess,
    OpenApiGetYandexMarketProductsSettingsUrl,
    OpenApiImportYandexMarketSetupDeleteMethod,
    type OpenApiImportYandexMarketSetupDeleteRequest,
    type OpenApiImportYandexMarketSetupDeleteResponseSuccess,
    OpenApiImportYandexMarketSetupDeleteUrl,
    OpenApiImportYandexMarketSetupGetMethod,
    type OpenApiImportYandexMarketSetupGetRequest,
    type OpenApiImportYandexMarketSetupGetResponseSuccess,
    OpenApiImportYandexMarketSetupGetUrl,
    OpenApiImportYandexMarketSetupMethod,
    type OpenApiImportYandexMarketSetupRequest,
    type OpenApiImportYandexMarketSetupResponseSuccess,
    OpenApiImportYandexMarketSetupUrl,
    OpenApiUpdateYandexMarketProductsSettingsMethod,
    type OpenApiUpdateYandexMarketProductsSettingsRequest,
    type OpenApiUpdateYandexMarketProductsSettingsResponseSuccess,
    OpenApiUpdateYandexMarketProductsSettingsUrl,
} from '#shared/api/schema';

const yandexMarketApi = createApi({
    name: 'entities/integration/api/yandexMarketApi',
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getSetup: builder.query<
                OpenApiImportYandexMarketSetupGetResponseSuccess,
                OpenApiImportYandexMarketSetupGetRequest
            >({
                query: () => {
                    return {
                        url: OpenApiImportYandexMarketSetupGetUrl,
                        method: OpenApiImportYandexMarketSetupGetMethod,
                    };
                },
            }),
            postSetup: builder.mutation<
                OpenApiImportYandexMarketSetupResponseSuccess,
                OpenApiImportYandexMarketSetupRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiImportYandexMarketSetupUrl,
                        method: OpenApiImportYandexMarketSetupMethod,
                        body: payload,
                    };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    yandexMarketApi.endpoints.getSetup.updateQueryData(undefined, () => data);
                },
            }),
            deleteSetup: builder.mutation<
                OpenApiImportYandexMarketSetupDeleteResponseSuccess,
                OpenApiImportYandexMarketSetupDeleteRequest
            >({
                query: () => {
                    return {
                        url: OpenApiImportYandexMarketSetupDeleteUrl,
                        method: OpenApiImportYandexMarketSetupDeleteMethod,
                    };
                },
            }),
            getCampaigns: builder.query<
                OpenApiGetYandexMarketCampaignsResponseSuccess,
                OpenApiGetYandexMarketCampaignsRequest
            >({
                query: () => {
                    return {
                        url: OpenApiGetYandexMarketCampaignsUrl,
                        method: OpenApiGetYandexMarketCampaignsMethod,
                    };
                },
            }),
            getProductsSettings: builder.query<
                OpenApiGetYandexMarketProductsSettingsResponseSuccess,
                OpenApiGetYandexMarketProductsSettingsRequest
            >({
                query: () => {
                    return {
                        url: OpenApiGetYandexMarketProductsSettingsUrl,
                        method: OpenApiGetYandexMarketProductsSettingsMethod,
                    };
                },
            }),
            updateProductsSettings: builder.mutation<
                OpenApiUpdateYandexMarketProductsSettingsResponseSuccess,
                OpenApiUpdateYandexMarketProductsSettingsRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiUpdateYandexMarketProductsSettingsUrl,
                        method: OpenApiUpdateYandexMarketProductsSettingsMethod,
                        body: payload,
                    };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    yandexMarketApi.endpoints.getProductsSettings.updateQueryData(undefined, () => data);
                },
            }),
        };
    },
});

export const integrationYandexMarketSetupGet = yandexMarketApi.endpoints.getSetup.query;
export const useIntegrationYandexMarketSetup = yandexMarketApi.useGetSetupQuery;
export const integrationYandexMarketSetupPost = yandexMarketApi.endpoints.postSetup.mutate;
export const integrationYandexMarketSetupDelete = yandexMarketApi.endpoints.deleteSetup.mutate;
export const useIntegrationYandexMarketCampaigns = yandexMarketApi.useGetCampaignsQuery;
export const useIntegrationYandexMarketProductsSettings = yandexMarketApi.useGetProductsSettingsQuery;
export const updateIntegrationYandexMarketProductsSettings = yandexMarketApi.endpoints.updateProductsSettings.mutate;
